import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem('c4p_access_token');
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await authAPI.me();
      setUser(data);
    } catch {
      localStorage.removeItem('c4p_access_token');
      localStorage.removeItem('c4p_refresh_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle SSO token delivered via query params after Okta callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken   = params.get('sso_token');
    const ssoRefresh = params.get('sso_refresh');
    const ssoError   = params.get('sso_error');

    if (ssoError) {
      const messages = {
        account_deactivated: 'Your account is deactivated. Please contact your administrator.',
        state_mismatch: 'SSO login failed - security check failed. Please try again.',
        token_exchange_failed: 'SSO login failed - could not connect to Okta. Please try again.',
        invalid_token: 'SSO login failed - invalid token received from Okta.',
        missing_params: 'SSO login failed - incomplete response from Okta.',
        default: 'SSO login failed. Please try again or use local login.',
      };
      setError(messages[ssoError] || messages.default);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      setLoading(false);
      return;
    }

    if (ssoToken) {
      localStorage.setItem('c4p_access_token', ssoToken);
      if (ssoRefresh) localStorage.setItem('c4p_refresh_token', ssoRefresh);
      // Clean the tokens from the URL immediately
      window.history.replaceState({}, '', window.location.pathname);
      fetchMe();
      return;
    }

    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    setError(null);
    try {
      const { data } = await authAPI.login(email, password);
      localStorage.setItem('c4p_access_token', data.access_token);
      localStorage.setItem('c4p_refresh_token', data.refresh_token);
      setUser(data.user);
      return { forceReset: data.force_reset_password, user: data.user };
    } catch (err) {
      const msg = err.response?.data?.detail || 'Login failed. Please try again.';
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try { await authAPI.logout(); } catch {}
    localStorage.removeItem('c4p_access_token');
    localStorage.removeItem('c4p_refresh_token');
    setUser(null);
  };

  const refreshUser = () => fetchMe();

  const hasRole = useCallback((roles) => {
    if (!user) return false;
    if (typeof roles === 'string') return user.role === roles;
    return roles.includes(user.role);
  }, [user]);

  const isAdmin    = user?.role === 'SUPER_ADMIN';
  const isManager  = user?.role === 'MANAGER' || isAdmin;
  const isAccounts = user?.role === 'ACCOUNTS';
  const isSalesOrAbove = !!user;

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      setError,
      login,
      logout,
      refreshUser,
      hasRole,
      isAdmin,
      isManager,
      isAccounts,
      isSalesOrAbove,
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
